/**
 * BugReportModal — Layer 2 user opt-in file upload for debug
 *
 * Triggered when translate fails. User can:
 *   1. Skip → just close modal
 *   2. Upload file with explicit consent → POST /api/bug-reports
 *
 * Privacy: Per Privacy Policy §9.2 — explicit checkbox + retention 14 days.
 */

import React, { useState, useRef } from 'react';
import { X, AlertTriangle, Upload, Check, FileText, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { track } from '../../services/analytics';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage?: string;
  fileName?: string;
  jobId?: string;
  /** Optional: pre-filled file (Blob) if app has it in memory; user can override via picker */
  preloadedFile?: File;
}

const CONSENT_TEXT =
  'Tôi đồng ý gửi file này cho dev của Dịch Tài Liệu để khắc phục lỗi. ' +
  'Hiểu rằng file sẽ lưu tối đa 14 ngày, KHÔNG dùng để training AI, KHÔNG share bên thứ ba. ' +
  'Có thể yêu cầu xóa bất cứ lúc nào (Privacy Policy §9.2).';

const BugReportModal: React.FC<BugReportModalProps> = ({
  isOpen, onClose, errorMessage, fileName, jobId, preloadedFile,
}) => {
  const { session } = useAuth();
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState<File | null>(preloadedFile ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!file || !consent) return;
    if (!session?.access_token) {
      setError('Bạn cần đăng nhập để gửi bug report.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('consentText', CONSENT_TEXT);
      if (jobId) formData.append('jobId', jobId);
      if (errorMessage) formData.append('errorMessage', errorMessage);

      const res = await fetch(`${WORKERS_URL}/api/bug-reports`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setSuccess(`Đã gửi báo cáo. Em sẽ xem + fix sớm. Cảm ơn anh/chị 🙏 (ID: ${data.id?.slice(0, 8)})`);
      track('credit_purchase_success', { source: 'bug_report' }); // Using existing event for now
    } catch (e: any) {
      setError(e.message || 'Gửi báo cáo thất bại. Bạn thử lại sau hoặc email support@dichtailieu.vn');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 bg-rose-50/50 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-rose-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Dịch file không thành công</h3>
              <p className="text-xs text-slate-500 mt-0.5">Em xin lỗi. Anh/chị có thể giúp em fix bug?</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Error context */}
          {(fileName || errorMessage) && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              {fileName && (
                <div className="flex items-center gap-2 text-slate-700 font-medium">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="truncate">{fileName}</span>
                </div>
              )}
              {errorMessage && (
                <p className="text-xs text-slate-500 mt-1.5 font-mono break-words">{errorMessage.slice(0, 200)}</p>
              )}
            </div>
          )}

          {success ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-800">{success}</p>
              </div>
            </div>
          ) : (
            <>
              {/* File picker */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">
                  File để dev reproduce bug
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.pptx,.pdf,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Chọn file
                  </button>
                  {file && (
                    <span className="text-xs text-slate-500 truncate flex-1">
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">DOCX, PPTX, PDF, TXT — max 10MB</p>
              </div>

              {/* Consent */}
              <label className="flex items-start gap-2.5 p-3 bg-emerald-50/30 border border-emerald-200/50 rounded-lg cursor-pointer hover:bg-emerald-50/50">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-emerald-600"
                />
                <span className="text-xs text-slate-700 leading-relaxed">
                  <Shield className="w-3.5 h-3.5 inline text-emerald-600 mr-1" />
                  Tôi đồng ý gửi file cho dev sửa lỗi. File lưu tối đa 14 ngày, KHÔNG dùng training AI, KHÔNG share bên thứ ba. Có thể yêu cầu xóa bất cứ lúc nào.{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800">
                    Privacy Policy §9.2
                  </a>
                </span>
              </label>

              {error && (
                <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            {success ? 'Đóng' : 'Bỏ qua'}
          </button>
          {!success && (
            <button
              onClick={handleSubmit}
              disabled={!file || !consent || submitting}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Đang gửi...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Gửi file để debug
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
